import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  PageHeading,
  Icon,
  IconButton,
  ConfirmDialog,
  Textarea,
  ExportExcel,
  hasPrivilege,
  PRIVILEGES,
  getEmployeeID,
  getCompanyID,
} from "../../Common/UIComponents";
import ConfigTable from "../../Common/Tables/ConfigTable";
import DashboardLayout from "../../Layout/DashboardLayout";
import { fetchProjects, closeProject, reopenProject } from "../../API_Service/Project_Slice";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const buildTableRows = (items = []) =>
  items.map((item, i) => ({
    id: item.ProjectID || `row-${i}`,
    ProjectID: item.ProjectID,
    ProjectName: item.ProjectName || "Untitled Project",
    ProjectCode: item.ProjectCode || "",
    TestType: item.TestType || "",
    TestName: item.TestName || "",
    TestStandard: item.TestStandard || "",
    BasicStandard: item.BasicStandard || "",
    Status: item.Status || "AC",
  }));

const EXPORT_COLUMNS = [
  { key: "ProjectName", title: "Project Name" },
  { key: "ProjectCode", title: "Project Code" },
  { key: "TestType", title: "Test Type" },
  { key: "TestName", title: "Test Name" },
  { key: "TestStandard", title: "Test Standard" },
  { key: "BasicStandard", title: "Basic Standard" },
  { key: "Status", title: "Status", format: (v) => v === "AC" ? "Active" : v === "DEL" ? "Inactive" : v || "" },
];

// ─────────────────────────────────────────────
// STATS ROW
// ─────────────────────────────────────────────

const StatsRow = ({ rows }) => {
  const total = rows.length;
 const activeProjects = rows.filter(
   (r) => r.Status === "AC" || r.Status === "In-Progress"
 ).length;
 const inactiveProjects = rows.filter(
   (r) => r.Status === "DEL" || r.Status === "Completed"
 ).length;

  const stats = [
    {
      label: "Total Projects",
      value: total,
      suffix: "",
      icon: "assignment",
      iconBg: "bg-[#005b60]/10",
      iconColor: "text-[#005b60]",
      sparkline: true,
    },
    {
      label: "Active Projects",
      value: activeProjects,
      suffix: "",
      icon: "check_circle",
      iconBg: "bg-[#006fa8]/10",
      iconColor: "text-[#006fa8]",
      sparkline: false,
    },
    {
      label: "Completed Projects",
      value: inactiveProjects,
      suffix: "",
      icon: "done",
       iconBg: "bg-[#006fa8]/10",
      iconColor: "text-[#006fa8]",
      sparkline: false,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
      {stats.map((s) => (
        <div
          key={s.label}
          className="bg-white p-4 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50"
        >
          <p className="text-[#3e494a] text-xs font-bold uppercase tracking-widest mb-2">
            {s.label}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${s.iconBg} rounded-lg ${s.iconColor}`}>
                <span className="material-symbols-outlined text-[20px]">{s.icon}</span>
              </div>
              <h3 className="text-2xl font-black text-[#181c1d]">
                {s.value}{s.suffix}
              </h3>
            </div>
            {s.sparkline && (
              <div className="w-24 h-10 bg-[#006fa8]/5 rounded overflow-hidden">
                <div
                  className="h-full w-full opacity-40 bg-gradient-to-r from-[#006fa8] to-transparent"
                  style={{
                    clipPath:
                      "polygon(0 100%, 10% 80%, 25% 90%, 40% 60%, 55% 75%, 70% 40%, 85% 55%, 100% 20%, 100% 100%)",
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// STATUS BADGE COMPONENT
// ─────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const getStatusDisplay = (status) => {
    switch(status) {
      case "AC":
        return { label: "Active", color: "bg-[#bbf46e]/20 text-[#112000]" };
      case "DEL":
        return { label: "Inactive", color: "bg-red-100 text-red-700" };
      case "In-Progress":
        return { label: "In-Progress", color: "bg-yellow-100 text-yellow-700" };
      case "Completed":
        return { label: "Completed", color: "bg-blue-100 text-blue-700" };
      default:
        return { label: status || "Active", color: "bg-[#bbf46e]/20 text-[#112000]" };
    }
  };

  const statusInfo = getStatusDisplay(status);
  return (
    <span
      className={`inline-block whitespace-nowrap px-3 py-1 rounded-full text-xs font-bold uppercase tracking-tight ${statusInfo.color}`}
    >
      {statusInfo.label}
    </span>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function ProjectManagementPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const employeeID = getEmployeeID();
  const companyId = getCompanyID();

  const { list: projects = [], loading = false } =
    useSelector((state) => state.project) || {};

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editSuccess, setEditSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [settingsError, setSettingsError] = useState(null);

  // ── Close Project confirmation state ──
  const [closeConfirm, setCloseConfirm] = useState({ open: false, project: null });
  const [closing, setClosing] = useState(false);

  const handleCloseClick = useCallback((row) => {
    setCloseConfirm({ open: true, project: row });
  }, []);

  const handleCloseCancel = useCallback(() => {
    setCloseConfirm({ open: false, project: null });
  }, []);

  const handleCloseConfirm = useCallback(async () => {
    if (!closeConfirm.project) return;
    setClosing(true);
    try {
      await dispatch(
        closeProject({
          projectId: closeConfirm.project.ProjectID,
          companyId,
          createdBy: employeeID,
        })
      ).unwrap();

      dispatch(fetchProjects(companyId));
      setSuccessMsg("Project closed successfully.");
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
      setCloseConfirm({ open: false, project: null });
    } catch (err) {
      setSettingsError(typeof err === "string" ? err : "Project could not be closed.");
      setCloseConfirm({ open: false, project: null });
    } finally {
      setClosing(false);
    }
  }, [closeConfirm.project, dispatch, companyId, employeeID]);

  // ── Reopen Project confirmation state (with Remarks) ──
  const [reopenConfirm, setReopenConfirm] = useState({ open: false, project: null });
  const [reopenRemarks, setReopenRemarks] = useState("");
  const [reopening, setReopening] = useState(false);

  const handleReopenClick = useCallback((row) => {
    setReopenRemarks("");
    setReopenConfirm({ open: true, project: row });
  }, []);

  const handleReopenCancel = useCallback(() => {
    setReopenConfirm({ open: false, project: null });
    setReopenRemarks("");
  }, []);

  const handleReopenConfirm = useCallback(async () => {
    if (!reopenConfirm.project) return;
    setReopening(true);
    try {
      await dispatch(
        reopenProject({
          projectId: reopenConfirm.project.ProjectID,
          companyId,
          remarks: reopenRemarks,
          createdBy: employeeID,
        })
      ).unwrap();

      dispatch(fetchProjects(companyId));
      setSuccessMsg("Project reopened successfully.");
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
      setReopenConfirm({ open: false, project: null });
      setReopenRemarks("");
    } catch (err) {
      setSettingsError(typeof err === "string" ? err : "Project could not be reopened.");
      setReopenConfirm({ open: false, project: null });
      setReopenRemarks("");
    } finally {
      setReopening(false);
    }
  }, [reopenConfirm.project, dispatch, companyId, employeeID, reopenRemarks]);

  useEffect(() => {
    if (companyId ) {
      dispatch(fetchProjects(companyId));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    if (location.state?.refresh) {
      dispatch(fetchProjects(companyId));
      if (location.state?.editSuccess) {
        setSuccessMsg(location.state?.isNew ? "Project Saved successfully." : "Project Updated successfully.");
        setEditSuccess(true);
        setTimeout(() => setEditSuccess(false), 3000);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh, location.pathname, navigate, dispatch, companyId]);

  const rows = useMemo(() => buildTableRows(projects), [projects]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.ProjectName?.toLowerCase().includes(q) ||
      r.ProjectCode?.toLowerCase().includes(q) ||
      r.Status?.toLowerCase().includes(q)
    );
  }, [searchQuery, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // ── Handlers ─────────────────────────────────
  const handleEdit = useCallback(
    (row) => navigate(`/project-management/edit/${row.ProjectID}`, { state: { mode: "edit", data: row } }),
    [navigate]
  );

  const handleAddNew = useCallback(
    () => navigate("/project-management/create", { state: { mode: "create" } }),
    [navigate]
  );

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // ── Row actions: Edit always; Close only when In-Progress; Reopen only when Completed ──
  const rowActions = useCallback((row) => (
    <>
      {hasPrivilege(PRIVILEGES.PROJ_EDIT) && (
        <IconButton icon="edit" label="Edit" onClick={() => handleEdit(row)} />
      )}
      {row.Status === "In-Progress" && (
        <IconButton
          icon="flag_circle"
          label="Close Project"
          onClick={() => handleCloseClick(row)}
          colorClass="text-[#3e494a] hover:text-red-600"
        />
      )}
      {row.Status === "Completed" && (
        <IconButton
          icon="undo"
          label="Reopen Project"
          onClick={() => handleReopenClick(row)}
          colorClass="text-[#3e494a] hover:text-[#08757C]"
        />
      )}
    </>
  ), [handleEdit, handleCloseClick, handleReopenClick]);

  // ── Columns ───────────────────────────────────
  const columns = useMemo(() => [
    {
      key: "ProjectName",
      title: "PROJECT NAME",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#005b60]/10 flex items-center justify-center text-[#005b60] flex-shrink-0">
            <span className="material-symbols-outlined text-[18px]">science</span>
          </div>
          <div className="min-w-0">
            <span
              className="font-bold text-[#181c1d] block max-w-[220px] truncate"
              title={row.ProjectName}
            >
              {row.ProjectName}
            </span>
            <span className="text-xs text-[#3e494a]">
              {row.ProjectCode}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "TestType",
      title: "TEST TYPE",
      render: (row) => {
        const testType = row.TestType;
        if (!testType || testType === "" || testType === "null") {
          return <span className="text-xs text-[#3e494a]">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {testType.split(",").map((type, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-[#e5e9e9] text-[#181c1d] rounded-full text-xs font-bold uppercase tracking-tight">
                {type.trim()}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "TestName",
      title: "TEST NAME",
      render: (row) => {
        const testName = row.TestName;
        if (!testName || testName === "" || testName === "null") {
          return <span className="text-xs text-[#3e494a]">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {testName.split(",").map((name, idx) => (
              <span key={idx} className="text-sm text-[#181c1d]">
                {name.trim()}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "TestStandard",
      title: "TEST STANDARD",
      render: (row) => {
        const testStandard = row.TestStandard;
        if (!testStandard || testStandard === "" || testStandard === "null") {
          return <span className="text-xs text-[#3e494a]">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {testStandard.split(",").map((standard, idx) => (
              <span key={idx} className="text-sm text-[#3e494a]">
                {standard.trim()}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "BasicStandard",
      title: "BASIC STANDARD",
      render: (row) => {
        const basicStandard = row.BasicStandard;
        if (!basicStandard || basicStandard === "" || basicStandard === "null") {
          return <span className="text-xs text-[#3e494a]">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {basicStandard.split(",").map((standard, idx) => (
              <span key={idx} className="text-sm text-[#3e494a]">
                {standard.trim()}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "Status",
      title: "STATUS",
      render: (row) => <StatusBadge status={row.Status} />,
    },
  ], []);

  if (loading && projects.length === 0) {
    return (
      <DashboardLayout>
        <div className="px-10 py-8 bg-[#f7fafa] min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#005b60]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="px-8 py-5 bg-[#f7fafa] text-[#181c1d] min-h-screen">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>Administration</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#08757C] font-semibold">Project Management</span>
        </nav>

        {/* Page Header */}
        <div className="flex flex-wrap justify-between items-end mb-5 gap-4">
          <PageHeading
            title="Project Management"
            subtitle="Manage clinical projects, monitor progress, and configure workflow parameters."
          />
          <div className="flex items-center gap-3">
            {hasPrivilege(PRIVILEGES.PROJ_CREATE) && (
              <Button variant="primary" onClick={handleAddNew}>
                <Icon name="add" className="mr-1 text-[20px]" />
                Add New Project
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <StatsRow rows={rows} />

        {/* Table */}
        <ConfigTable
          columns={columns}
          rows={paginatedRows}
          rowActions={rowActions}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="projects"
          emptyText="No projects found."
        />

        {settingsError && (
          <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">{settingsError}</div>
        )}
      </div>

      {/* Edit Success Toast */}
      {editSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          {successMsg}
        </div>
      )}

      {/* Close Project Confirmation Modal */}
      <ConfirmDialog
        isOpen={closeConfirm.open}
        title="Close Project"
        message={
          <>
            <p className="text-sm text-[#181c1d]">
              Do you really want to close the project "{closeConfirm.project?.ProjectName}"?
            </p>
            <p className="text-sm text-[#6e797a] mt-1">
              Once closed, it won't be bookable.
            </p>
          </>
        }
        confirmLabel="Yes, Close"
        cancelLabel="No"
        variant="warning"
        isLoading={closing}
        onConfirm={handleCloseConfirm}
        onCancel={handleCloseCancel}
      />

      {/* Reopen Project Confirmation Modal (with Remarks) */}
      <ConfirmDialog
        isOpen={reopenConfirm.open}
        title="Reopen Project"
        message={
          <div className="text-left">
            <p className="text-sm text-[#181c1d] mb-3">
              Do you want to reopen the project "{reopenConfirm.project?.ProjectName}"?
            </p>
            <Textarea
              id="reopen-remarks"
              value={reopenRemarks}
              onChange={(e) => setReopenRemarks(e.target.value)}
              placeholder="Reason for reopening (optional)"
              rows={3}
            />
          </div>
        }
        confirmLabel="Yes, Reopen"
        cancelLabel="No"
        variant="info"
        isLoading={reopening}
        onConfirm={handleReopenConfirm}
        onCancel={handleReopenCancel}
      />
    </DashboardLayout>
  );
}